import React, { useState, useEffect } from 'react';
import Modal from '../../../components/common/Modal';
import { testEmailSettings } from '../api/emailApi';

const EmailSettingsForm = ({ isOpen, onClose, onSubmit, isInline = false, initialData = null }) => {
  const [formData, setFormData] = useState({
    emailProtocol: 'starttls',
    sendFromAddress: '',
    smtpHost: '',
    smtpUser: '',
    smtpPassword: '',
    smtpPort: '587',
    companyName: '',
    testEmailTo: ''
  });

  const [isTestLoading, setIsTestLoading] = useState(false);
  const [testResult, setTestResult] = useState(null);

  // Perbaiki useEffect untuk memuat data dengan benar
  useEffect(() => {
    if (initialData && isOpen) {
      console.log('Loading initial data:', initialData);
      setFormData({
        emailProtocol: initialData.emailProtocol || 'starttls',
        sendFromAddress: initialData.sendFromAddress || '',
        smtpHost: initialData.smtpHost || '',
        smtpUser: initialData.smtpUser || '',
        smtpPassword: '', // Selalu kosongkan password untuk keamanan
        smtpPort: initialData.smtpPort?.toString() || '587',
        companyName: initialData.companyName || '',
        testEmailTo: ''
      });
    } else if (!initialData && isOpen) {
      // Reset form jika tidak ada data
      setFormData({
        emailProtocol: 'starttls',
        sendFromAddress: '',
        smtpHost: '',
        smtpUser: '',
        smtpPassword: '',
        smtpPort: '587',
        companyName: '',
        testEmailTo: ''
      });
    }
  }, [initialData, isOpen]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    console.log('Input change:', name, value); // Debug log
    
    if (name === 'emailProtocol') {
      let defaultPort = '587';
      if (value === 'smtps') {
        defaultPort = '465';
      } else if (value === 'smtp') {
        defaultPort = '25';
      }
      
      setFormData(prev => ({
        ...prev,
        [name]: value,
        smtpPort: defaultPort
      }));
    } else if (name === 'smtpPort') {
      let protocol = 'starttls';
      if (value === '465') {
        protocol = 'smtps';
      } else if (value === '25') {
        protocol = 'smtp';
      }
      
      setFormData(prev => ({
        ...prev,
        [name]: value,
        emailProtocol: protocol
      }));
    } else {
      setFormData(prev => {
        const newData = {
          ...prev,
          [name]: value
        };
        console.log('Updated form data:', newData); // Debug log
        return newData;
      });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    console.log('Form submitted with data:', formData);
    
    // Validasi form sebelum submit
    if (!formData.sendFromAddress || !formData.smtpHost || !formData.smtpUser || !formData.smtpPassword) {
      alert('Please fill in all required fields');
      return;
    }
    
    onSubmit(formData);
  };

  const handleTestEmail = async () => {
    if (!formData.testEmailTo) {
      alert('Please enter a test email address');
      return;
    }

    setIsTestLoading(true);
    setTestResult(null);

    try {
      const testData = {
        testEmail: formData.testEmailTo
      };
      
      const result = await testEmailSettings(testData);
      
      setTestResult({
        success: result.success,
        message: result.message
      });
    } catch (error) {
      setTestResult({
        success: false,
        message: 'Error sending test email: ' + error.message
      });
    } finally {
      setIsTestLoading(false);
    }
  };

  const handleCancel = () => {
    if (!isInline) {
      onClose();
    }
  };

  // Debug: Log form data setiap kali berubah
  useEffect(() => {
    console.log('Current form data:', formData);
  }, [formData]);

  // Debug: Log password changes
  useEffect(() => {
    console.log('Password field value:', formData.smtpPassword);
  }, [formData.smtpPassword]);

  const formContent = (
    <form id="modal-email-settings" onSubmit={handleSubmit} noValidate>
      <div className="modal-body">
        <div className="inv-section-label">
          <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>
          SMTP Configuration
        </div>
        <div className="row">
          <div className="col-md-6">
            <div className="mb-3">
              <label className="form-label">Email Protocol</label>
              <select
                className="form-select"
                name="emailProtocol"
                value={formData.emailProtocol}
                onChange={handleInputChange}
              >
                <option value="starttls">STARTTLS (Port 587)</option>
                <option value="smtps">SMTPS (Port 465)</option>
                <option value="smtp">SMTP (Port 25)</option>
              </select>
            </div>
          </div>
          <div className="col-md-6">
            <div className="mb-3">
              <label className="form-label">Send From Address *</label>
              <input
                type="email"
                className="form-control"
                name="sendFromAddress"
                value={formData.sendFromAddress}
                onChange={handleInputChange}
                placeholder="noreply@yourcompany.com"
                required
              />
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-md-6">
            <div className="mb-3">
              <label className="form-label">SMTP Host *</label>
              <input
                type="text"
                className="form-control"
                name="smtpHost"
                value={formData.smtpHost}
                onChange={handleInputChange}
                placeholder="smtp.hostinger.com"
                required
              />
            </div>
          </div>
          <div className="col-md-6">
            <div className="mb-3">
              <label className="form-label">SMTP Port</label>
              <input
                type="number"
                className="form-control"
                name="smtpPort"
                value={formData.smtpPort}
                onChange={handleInputChange}
                placeholder="587"
              />
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-md-6">
            <div className="mb-3">
              <label className="form-label">SMTP Username *</label>
              <input
                type="text"
                className="form-control"
                name="smtpUser"
                value={formData.smtpUser}
                onChange={handleInputChange}
                placeholder="info@yourdomain.com"
                required
              />
            </div>
          </div>
          <div className="col-md-6">
            <div className="mb-3">
              <label className="form-label">SMTP Password *</label>
              <input
                type="password"
                className="form-control"
                name="smtpPassword"
                value={formData.smtpPassword}
                onChange={handleInputChange}
                placeholder="Enter your password"
                required
                autoComplete="new-password"
                key={`password-${isOpen}`} // Tambahkan key untuk force re-render
              />
              <small className="form-text text-muted">
                {initialData ? 'Enter new password to update, or leave empty to keep current password' : 'Enter your SMTP password'}
              </small>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-md-6">
            <div className="mb-3">
              <label className="form-label">Company Name</label>
              <input
                type="text"
                className="form-control"
                name="companyName"
                value={formData.companyName}
                onChange={handleInputChange}
                placeholder="Your Company Name"
              />
            </div>
          </div>
          <div className="col-md-6">
            <div className="mb-3">
              <label className="form-label">Test Email Address</label>
              <input
                type="email"
                className="form-control"
                name="testEmailTo"
                value={formData.testEmailTo}
                onChange={handleInputChange}
                placeholder="test@example.com"
              />
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-md-6">
            <div className="mb-3">
              <label className="form-label">&nbsp;</label>
              <div>
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  onClick={handleTestEmail}
                  disabled={isTestLoading || !formData.testEmailTo}
                >
                  {isTestLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Sending...
                    </>
                  ) : (
                    'Test Send Email'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {testResult && (
          <div className={`alert alert-${testResult.success ? 'success' : 'danger'} mb-3`}>
            {testResult.message}
          </div>
        )}
      </div>

      <div className="inv-footer">
        <span className="text-muted me-auto" style={{ fontSize: '0.75rem' }}>
          <span style={{ color: '#9aa5b1' }}>
            <span className="inv-required-star">*</span> Required fields
          </span>
        </span>
        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={handleCancel}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="btn btn-primary btn-inv-save"
          disabled={!formData.sendFromAddress || !formData.smtpHost || !formData.smtpUser || !formData.smtpPassword}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
          Save Settings
        </button>
      </div>
    </form>
  );

  if (isInline) {
    return (
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Email Settings</h3>
        </div>
        <div className="card-body">
          {formContent}
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        #modal-email-settings .inv-section-label {
          font-size: 0.7rem; font-weight: 700; letter-spacing: 0.08em;
          text-transform: uppercase; color: #6c7a7d; margin-bottom: 0.75rem;
          display: flex; align-items: center; gap: 0.4rem;
        }
        #modal-email-settings .inv-section-label::after {
          content: ''; flex: 1; height: 1px; background: #e9ecef;
        }
        #modal-email-settings .inv-footer {
          border-top: 1px solid #e9ecef !important;
          background: #fff;
          padding: 1rem 1.5rem;
          display: flex; align-items: center; justify-content: flex-end; gap: 0.6rem;
          flex-shrink: 0;
        }
        #modal-email-settings .btn-inv-save {
          display: inline-flex; align-items: center; gap: 0.4rem;
        }
        #modal-email-settings .btn-inv-save:disabled {
          opacity: 0.6; cursor: not-allowed;
        }
        #modal-email-settings .inv-required-star { color: #e53935; margin-left: 2px; }
      `}</style>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title="Email Settings"
        size="lg"
      >
        {formContent}
      </Modal>
    </>
  );
};

export default EmailSettingsForm; 